import {
  BarChart3,
  Building2,
  GitMerge,
  LayoutDashboard,
  LogOut,
  Mail,
  Moon,
  Plug,
  Search,
  Settings,
  Sun,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { cn } from '../../lib/cn';
import { useLogout, useMe, useSwitchWorkspace } from '../../lib/session';
import { Badge, Spinner } from '../ui/feedback';
import { Select } from '../ui/form';

const NAV = [
  { to: '/', label: 'Home', icon: LayoutDashboard, end: true },
  { to: '/research/new', label: 'New research', icon: Search, end: false },
  { to: '/accounts', label: 'Businesses', icon: Building2, end: false },
  { to: '/people', label: 'People', icon: Users, end: false },
  { to: '/analytics', label: 'Insights', icon: BarChart3, end: false },
  { to: '/outreach', label: 'Outreach', icon: Mail, end: false },
  { to: '/duplicates', label: 'Duplicates', icon: GitMerge, end: false },
  { to: '/sources', label: 'Sources', icon: Plug, end: false },
  { to: '/settings', label: 'Settings', icon: Settings, end: false },
] as const;

type Theme = 'light' | 'dark' | 'system';

function readTheme(): Theme {
  try {
    const stored = localStorage.getItem('atlas-theme');
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
  try {
    if (theme === 'system') localStorage.removeItem('atlas-theme');
    else localStorage.setItem('atlas-theme', theme);
  } catch {
    // storage may be unavailable; the in-memory choice still applies
  }
}

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  useEffect(() => applyTheme(theme), [theme]);
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <button
      type="button"
      className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-control)] text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {isDark ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
    </button>
  );
}

/** Application frame: sidebar navigation, workspace switcher and the routed page. */
export function AppShell() {
  const me = useMe();
  const logout = useLogout();
  const switchWorkspace = useSwitchWorkspace();
  const navigate = useNavigate();

  if (me.isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-muted-foreground">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  if (!me.data) return null;

  const { user, workspace, workspaces } = me.data;

  return (
    <div className="flex min-h-dvh bg-background">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <aside className="hidden w-56 shrink-0 flex-col border-r border-border bg-surface md:flex">
        <div className="flex h-12 items-center gap-2 border-b border-border px-3">
          {/* The 2.0 lockup: wordmark with the lime dot, as on elevate2point0.com. */}
          <span className="font-display text-lg leading-none font-extrabold tracking-tight text-foreground">
            2<span className="text-accent">.</span>0
          </span>
          <span className="text-[13px] font-medium tracking-tight text-muted-foreground">Research</span>
          {workspace.kind === 'demo' ? <Badge tone="warning">Demo</Badge> : null}
        </div>

        <nav className="flex-1 space-y-0.5 p-2" aria-label="Main">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-[var(--radius-control)] px-2.5 py-1.5 text-[13px] font-medium transition-colors duration-150',
                  isActive ? 'bg-accent-subtle text-accent' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t border-border p-2">
          {workspaces.length > 1 ? (
            <label className="block">
              <span className="sr-only">Workspace</span>
              <Select
                className="h-8 text-[13px]"
                value={workspace.id}
                onChange={(event) => switchWorkspace.mutate(event.target.value)}
                disabled={switchWorkspace.isPending}
              >
                {workspaces.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                    {entry.kind === 'demo' ? ' (demo)' : ''}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-foreground">{user.name}</p>
              <p className="truncate text-[11px] text-muted-foreground">{user.email}</p>
            </div>
            <div className="flex items-center gap-0.5">
              <ThemeToggle />
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-control)] text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground"
                aria-label="Sign out"
                onClick={() =>
                  logout.mutate(undefined, {
                    onSuccess: () => {
                      void navigate('/login', { replace: true });
                    },
                  })
                }
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      <main id="main" className="min-w-0 flex-1">
        <Outlet />
      </main>
    </div>
  );
}
